import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

// Enhanced client configuration with connection pooling and performance optimizations
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: {
      'x-client-info': 'job-board-app',
    },
  },
  // Connection pooling configuration
  realtime: {
    params: {
      eventsPerSecond: 10, // Rate limiting for realtime events
    },
  },
  // Performance optimizations
  fetch: {
    // Enable HTTP/2 multiplexing
    keepalive: true,
  },
});

// Query builder with automatic caching and optimization
export const createOptimizedQuery = (table) => {
  return {
    select: (columns = '*', options = {}) => {
      const query = supabase?.from(table)?.select(columns, {
        count: options?.count || null,
        head: options?.head || false,
      });
      
      return {
        ...query,
        // Add caching capability
        withCache: (ttl = 60000) => {
          const cacheKey = `${table}_${columns}_${JSON.stringify(options)}`;
          return query;
        },
        // Add pagination helper
        paginate: (page = 1, pageSize = 20) => {
          const from = (page - 1) * pageSize;
          const to = from + pageSize - 1;
          return query?.range(from, to);
        },
      };
    },
  };
};

// Batch query executor to reduce N+1 queries
export const batchQuery = async (queries) => {
  try {
    const results = await Promise.all(queries);
    return results?.map((result, index) => ({
      data: result?.data,
      error: result?.error,
      index,
    }));
  } catch (error) {
    console.error('Batch query error:', error);
    throw error;
  }
};

// Connection health check
export const checkConnection = async () => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('id')?.limit(1);
    return { healthy: !error, error };
  } catch (error) {
    return { healthy: false, error };
  }
};

// Query performance tracker
const queryMetrics = new Map();

export const trackQuery = async (queryName, queryFn) => {
  const startTime = performance.now();
  try {
    const result = await queryFn();
    const duration = performance.now() - startTime;
    
    // Track metrics
    if (!queryMetrics?.has(queryName)) {
      queryMetrics?.set(queryName, { count: 0, totalTime: 0, avgTime: 0 });
    }
    
    const metrics = queryMetrics?.get(queryName);
    metrics.count++;
    metrics.totalTime += duration;
    metrics.avgTime = metrics?.totalTime / metrics?.count;
    
    // Log slow queries (>500ms)
    if (duration > 500) {
      console.warn(`Slow query detected: ${queryName} took ${duration?.toFixed(2)}ms`);
    }
    
    return result;
  } catch (error) {
    console.error(`Query error in ${queryName}:`, error);
    throw error;
  }
};

// Get query performance metrics
export const getQueryMetrics = () => {
  return Array.from(queryMetrics?.entries())?.map(([name, metrics]) => ({
    name,
    ...metrics,
  }));
};

// Clear query metrics
export const clearQueryMetrics = () => {
  queryMetrics?.clear();
};

export default supabase;